/*
 * Name       : player.js (Player Class)
 * Create On  : Jan 26, 2022
 * Author     : Frank Post
 * Version    : n/a
 * Copyright  : 2022 GrayV (Sauce Industries, LLS)
 * Description: This class stores and manages player data.
 */

const PlayerType = {
    Physical: '1',
    Virtual: '2',
    Mobile: '3',
    WSL : '4'
};

export default class Player {

    esTitle;
    locationZoneId;
    contactId;
    locationZonePlayerId;
    storeNumber;
    playerTypeId;
    playerType;
    lastCheckin;
    volume;
    currentPlaylistName;
    currentCustomListId;
    playerId;
    canShuffle;
    canSkip;
    canVote;
    clientDidSetMuted;
    isShutdown;
    locationId;
    locationName;
    locationZoneName;
    startupDate;
    demoSignupStatusId;
    updatedAt;
    version;
    stationCode;
    networkConfig;
    currentTrack;
    lastFive;

    constructor(data) {
        try {
            Object.keys(this).forEach(key => {
                if (!data[key] || data[key] == '') {
                    return;
                }
                this[key] = data[key];
            });
            this.setDefaultVotes();
        } catch(error) {
            console.log('error - invalid data object in Player instance');
        }
    }

    /**
     * Update the Player's attributes.
     * 
     * @param {*} data - json object with player data.
     */
    update(data) {
        try {
            Object.keys(this).forEach(key => {
                if (!data[key] || data[key] == '') {
                    return;
                }
                this[key] = data[key];
            });
            this.setDefaultVotes(); // T666 - (05-10-2022)
        } catch(error) {
            console.log('error - invalid data object in Player update method');
        }
    }

    /*
     * Determines if a player should be deemed online or offline.
     *
     * @return bool - returns true if player is online, otherwise returns false
     */
    isOnline() {
        // sanity check
        if (this.lastCheckin == null || this.lastCheckin == undefined) {
            return false;
        }

        let diff = this.checkinStatus();
        return diff > 900 ? false : true; // limit = 15 minutes (900 sec)
        // return diff > (86400 * 7) ? false : true; // limit = 1 day for testing ONLY - TODO: Uncomment above when testing completed and remove this line
    }

    /*
     * Determines if a player is currently in 'pending' status.
     *
     * Rules (per Lori) for 'pending' status:
     *   1) Only physical players (excludes Virtual, Mobile, and WSL players)
     *   2) The 'demoSignupStatusId is 4 or 16.
     *
     * @return bool - returns true if player is in 'pending' status, otherwise returns false
     */
    isPending() {
        if (!this.isPhysicalPlayer()) {
            return false; // not a physical player (pending status does not apply)
        }
        if (this.demoSignupStatusId == null || this.demoSignupStatusId == undefined) {
            return false; // default - player is not in 'pending' status
        }
        return (this.demoSignupStatusId == 4 || this.demoSignupStatusId == 16); // 4 or 16 indicates player is in 'pending' status
    }

    /*
     * Return a unique ID according to the playerTypeId
     *
     * @return string - return either locationZonePlayerId or contactId
     */
    fId() {
        if (this.isPhysicalPlayer() || this.isVirtualPlayer()) {
            return this.locationZonePlayerId;
        } else {
            // is a mobile or web player
            return this.contactId;
        }
    }

    /*
     * Insert vote information/status into currentTrack and lastFive.
     *
     * NOTE: Votes were takened out of the player schema to allow for a separate endpoint
     *       call to retrieve vote information. By previous design votes are tighly coupled
     *       to track information which resides in the player schema. For a more seemless
     *       implementation and avoid major changes to the logic of displaying/fetching votes,
     *       the vote information is inserted into the player class once votes information if fetched.
     * 
     * @param {*} data - json object with votes data. (response.data.data.votes)
     */
    insertVotes(data) {
        try {
            if (!data) {
                return; // No data
            }

            const trackVotes = data;

            // Set currentTrack
            Object.keys(trackVotes).forEach(key => {
                if(this.currentTrack.code.toLowerCase() == key.toLowerCase()) {
                    this.currentTrack.votes = trackVotes[key].votes;
                }
            });

            // Set lastFive track votes
            for (let i = 0; i < this.lastFive.length; i++) {
                Object.keys(trackVotes).forEach(key => {
                    if(this.lastFive[i].code.toLowerCase() == key.toLowerCase()) {
                        this.lastFive[i].votes = trackVotes[key].votes;
                    }
                });
            }
        } catch(error) {
            console.log(`EXCEPTION WHILE SETTING VOTES IN PLAYER OBJECT: votes -> ${JSON.stringify(data)} error -> ${error}`);
        }
    }

    /*
     * Create and initialize default values for currentTrack and lastFive votes if defined.
     */
    setDefaultVotes() {
        if (this.currentTrack != null) {
            this.currentTrack.votes = { vote: null, voteReasonId: null, comment: null };
        }
        if (this.lastFive != null) {
            Object.keys(this.lastFive).forEach(key => {
                this.lastFive[key].votes = { vote: null, voteReasonId: null, comment: null };
            });
        }
    }

    /*
     * Uses location and unique ID to create RLID based on Paul's algorythm.
     * <reference 'returnRLID' algorithm in clientwebapi->VotingLinksController>
     * 
     * NOTE: This method may be depricated once dependancies of 'rlid' is remove from api calls
     *  
     * @return string rlid
     */
    rlid() {
        let fId   = this.pad(this.fId());
        let locationId = this.pad(this.locationId);
        let middle     = fId.length + 1;
        //always based on a 2 char string for middle..tacked onto the end
        if(middle.length < 2) middle = '0' + middle;
        let rlid = fId + locationId + middle;

        return rlid;
    }

    /*
     * Inserts numeric values systematically into a given string (used is the rlid production process).
     * <reference 'pad' algorithm in clientwebapi->VotingLinksController>
     * 
     * NOTE: This method may be depricated once dependancies of 'rlid' is remove from api calls
     * 
     * @param str - string to pad
     * @return string
     */
    pad(str) {
        let result = '';
        for(let i = 0; i < str.length; i++) {
            let letter = str.substr(i , 1);
            result = result + letter + i;
        }
            
        return result;
    }

    /*
     * Returns time difference in seconds between 'lastCheckin' and current time.
     *
     * @return integer - returns difference between 2 epoch times.
     */
    checkinStatus() {
        return Math.floor(Date.now()/1000) - parseInt(this.lastCheckin);
    }

    isPhysicalPlayer() {
        return (this.playerTypeId == PlayerType.Physical);
    }

    isVirtualPlayer() {
        return (this.playerTypeId == PlayerType.Virtual);
    }

    isMobilePlayer() {
        return (this.playerTypeId == PlayerType.Mobile);
    }

    isWSLPlayer() {
        return (this.playerTypeId == PlayerType.WSL);
    }
}